import type { Dispatch, SetStateAction } from "react";
import { create } from "zustand";
import { Timestamp } from "firebase/firestore";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { ActivityActor } from "src/lib/activityService";
import {
  createHappeningNowItem,
  createHappeningNowLiveItem,
  deleteHappeningNowItem,
  deleteLiveHappeningNowItem,
  listHappeningNowItems,
  listLiveHappeningNowItems,
  reorderHappeningNowItems,
  reorderLiveHappeningNowItems,
  setHappeningNowActive,
  setLiveHappeningNowActive,
  timestampToDatetimeLocalValue,
  updateHappeningNowItem,
  updateLiveHappeningNowItem,
} from "src/lib/happeningNowService";
import {
  clearDraft as clearDraftService,
  getCarouselPublication,
  seedDraftFromLive,
} from "src/lib/carouselPublicationService";
import {
  draftByIdFromList,
  editFromLiveIsUnchanged,
  liveCardsRemovedOnPublish,
} from "src/lib/happeningNowPanelLogic";
import type { EditHappeningNowFormState } from "src/components/HappeningNow/EditHappeningNowDialog";
import type { HappeningNowListItem } from "src/types/happeningNow";
import type {
  CarouselPublicationDoc,
  CarouselPublicationListItem,
} from "src/types/carouselPublication";
import { useCarouselPublicationsStore } from "src/store/carouselPublicationsStore";
import {
  platformBooleansFromDoc,
  platformsFromBooleans,
} from "src/lib/happeningNowPlatforms";

export type HappeningNowCreateForm = {
  title: string;
  description: string;
  targetLink: string;
  startsAtLocal: string;
  expiresAtLocal: string;
  active: boolean;
  platformAndroid: boolean;
  platformIos: boolean;
};

const emptyCreateForm = (): HappeningNowCreateForm => ({
  title: "",
  description: "",
  targetLink: "",
  startsAtLocal: "",
  expiresAtLocal: "",
  active: true,
  platformAndroid: true,
  platformIos: true,
});

export type HappeningNowPanelStore = {
  /** Draft carousel items */
  items: HappeningNowListItem[];
  liveItems: HappeningNowListItem[];
  loading: boolean;
  saving: boolean;
  savingLive: boolean;
  error: string | null;
  success: string | null;
  busyId: string | null;
  liveBusyId: string | null;

  createForm: HappeningNowCreateForm;
  imageFile: File | null;
  imageError: string | null;
  formError: string | null;

  edit: EditHappeningNowFormState | null;
  editError: string | null;
  editSaving: boolean;
  editDeleting: boolean;
  editFromLiveOriginal: HappeningNowListItem | null;

  publishing: boolean;
  seeding: boolean;
  clearing: boolean;

  previewPub: CarouselPublicationDoc | null;
  republishTarget: CarouselPublicationDoc | null;
  republishSubmitting: boolean;
  republishError: string | null;
  pubBusyId: string | null;

  patchCreateForm: (patch: Partial<HappeningNowCreateForm>) => void;
  setImageFile: (file: File | null) => void;
  setImageError: (msg: string | null) => void;
  resetCreateForm: () => void;

  setEditForm: Dispatch<SetStateAction<EditHappeningNowFormState | null>>;

  clearSuccess: () => void;
  clearError: () => void;
  reset: () => void;

  load: () => Promise<void>;

  submitCreateDraft: (
    uid: string,
    creatorDisplayName: string,
    actor: ActivityActor | null,
  ) => Promise<void>;

  submitCreateLive: (
    uid: string,
    creatorDisplayName: string,
    actor: ActivityActor | null,
  ) => Promise<void>;

  openEdit: (row: HappeningNowListItem) => void;
  openEditFromLive: (live: HappeningNowListItem) => void;
  closeEdit: () => void;

  saveEdit: (
    file: File | null,
    uid: string,
    actor: ActivityActor | null,
  ) => Promise<void>;

  deleteEdit: (actor: ActivityActor | null) => Promise<void>;

  toggleDraftActive: (
    item: HappeningNowListItem,
    actor: ActivityActor | null,
  ) => Promise<void>;

  deleteDraft: (
    item: HappeningNowListItem,
    actor: ActivityActor | null,
  ) => Promise<void>;

  onDraftDragEnd: (
    event: DragEndEvent,
    actor: ActivityActor | null,
  ) => Promise<void>;

  toggleLiveActive: (
    item: HappeningNowListItem,
    actor: ActivityActor | null,
  ) => Promise<void>;

  onLiveDragEnd: (
    event: DragEndEvent,
    actor: ActivityActor | null,
  ) => Promise<void>;

  publishCarousel: (
    uid: string,
    actor: ActivityActor | null,
    opts?: { skipConfirm?: boolean },
  ) => Promise<void>;

  seedFromLive: (uid: string, actor: ActivityActor | null) => Promise<void>;

  clearDraft: (actor: ActivityActor | null) => Promise<void>;

  loadPublicationPreview: (pub: CarouselPublicationListItem) => Promise<void>;
  closePublicationPreview: () => void;

  openRepublish: (pub: CarouselPublicationListItem) => Promise<void>;
  closeRepublish: () => void;

  confirmRepublish: (
    params: { includeCardIds: string[]; newExpiresAtLocal: string },
    uid: string,
    actor: ActivityActor | null,
  ) => Promise<void>;

  togglePublicationStar: (
    pub: CarouselPublicationListItem,
    uid: string,
    actor: ActivityActor | null,
  ) => Promise<void>;

  deletePublication: (
    pub: CarouselPublicationListItem,
    actor: ActivityActor | null,
  ) => Promise<void>;
};

const snapshot = (): Omit<
  HappeningNowPanelStore,
  | "patchCreateForm"
  | "setImageFile"
  | "setImageError"
  | "resetCreateForm"
  | "setEditForm"
  | "clearSuccess"
  | "clearError"
  | "reset"
  | "load"
  | "submitCreateDraft"
  | "submitCreateLive"
  | "openEdit"
  | "openEditFromLive"
  | "closeEdit"
  | "saveEdit"
  | "deleteEdit"
  | "toggleDraftActive"
  | "deleteDraft"
  | "onDraftDragEnd"
  | "toggleLiveActive"
  | "onLiveDragEnd"
  | "publishCarousel"
  | "seedFromLive"
  | "clearDraft"
  | "loadPublicationPreview"
  | "closePublicationPreview"
  | "openRepublish"
  | "closeRepublish"
  | "confirmRepublish"
  | "togglePublicationStar"
  | "deletePublication"
> => ({
  items: [],
  liveItems: [],
  loading: true,
  saving: false,
  savingLive: false,
  error: null,
  success: null,
  busyId: null,
  liveBusyId: null,
  createForm: emptyCreateForm(),
  imageFile: null,
  imageError: null,
  formError: null,
  edit: null,
  editError: null,
  editSaving: false,
  editDeleting: false,
  editFromLiveOriginal: null,
  publishing: false,
  seeding: false,
  clearing: false,
  previewPub: null,
  republishTarget: null,
  republishSubmitting: false,
  republishError: null,
  pubBusyId: null,
});

export const useHappeningNowPanelStore = create<HappeningNowPanelStore>(
  (set, get) => ({
    ...snapshot(),

    patchCreateForm: (patch) =>
      set((s) => ({ createForm: { ...s.createForm, ...patch } })),

    setImageFile: (file) => set({ imageFile: file }),
    setImageError: (msg) => set({ imageError: msg }),

    resetCreateForm: () =>
      set({
        createForm: emptyCreateForm(),
        imageFile: null,
        imageError: null,
      }),

    setEditForm: (updater) =>
      set((s) => ({
        edit:
          typeof updater === "function"
            ? (
                updater as (
                  p: EditHappeningNowFormState | null,
                ) => EditHappeningNowFormState | null
              )(s.edit)
            : updater,
      })),

    clearSuccess: () => set({ success: null }),
    clearError: () => set({ error: null }),

    reset: () => set(snapshot()),

    load: async () => {
      set({ loading: true, error: null });
      try {
        const [drafts, live] = await Promise.all([
          listHappeningNowItems(),
          listLiveHappeningNowItems(),
        ]);
        set({ items: drafts, liveItems: live, loading: false });
      } catch (e: unknown) {
        set({
          error: e instanceof Error ? e.message : "Failed to load cards",
          loading: false,
        });
      }
    },

    submitCreateDraft: async (uid, creatorDisplayName, actor) => {
      const { createForm, imageFile } = get();
      set({ formError: null, success: null });
      if (!imageFile) {
        set({ formError: "Pick an image first." });
        return;
      }
      set({ saving: true });
      try {
        const platforms = platformsFromBooleans(
          createForm.platformAndroid,
          createForm.platformIos,
        );
        if (!platforms) {
          set({
            formError: "Pick at least one platform — Android and/or iOS.",
          });
          set({ saving: false });
          return;
        }
        await createHappeningNowItem(
          {
            title: createForm.title,
            description: createForm.description || undefined,
            targetLink: createForm.targetLink,
            startsAtLocal: createForm.startsAtLocal || undefined,
            expiresAtLocal: createForm.expiresAtLocal,
            active: createForm.active,
            platforms,
            imageFile,
            creatorDisplayName,
          },
          uid,
          actor,
        );
        set({
          success:
            "Card added to draft. Click Publish carousel when you're ready.",
        });
        get().resetCreateForm();
        await get().load();
      } catch (err: unknown) {
        set({
          formError:
            err instanceof Error ? err.message : "Failed to add to draft",
        });
      } finally {
        set({ saving: false });
      }
    },

    submitCreateLive: async (uid, creatorDisplayName, actor) => {
      const { createForm, imageFile } = get();
      set({ formError: null, success: null });
      if (!imageFile) {
        set({ formError: "Pick an image first." });
        return;
      }
      set({ savingLive: true });
      try {
        const platforms = platformsFromBooleans(
          createForm.platformAndroid,
          createForm.platformIos,
        );
        if (!platforms) {
          set({
            formError: "Pick at least one platform — Android and/or iOS.",
          });
          set({ savingLive: false });
          return;
        }
        await createHappeningNowLiveItem(
          {
            title: createForm.title,
            description: createForm.description || undefined,
            targetLink: createForm.targetLink,
            startsAtLocal: createForm.startsAtLocal || undefined,
            expiresAtLocal: createForm.expiresAtLocal,
            active: createForm.active,
            platforms,
            imageFile,
            creatorDisplayName,
          },
          uid,
          actor,
        );
        set({
          success:
            "Card added to the live carousel — visible in the app now. It was not added to the draft.",
        });
        get().resetCreateForm();
        await get().load();
      } catch (err: unknown) {
        set({
          formError:
            err instanceof Error
              ? err.message
              : "Could not add to live carousel",
        });
      } finally {
        set({ savingLive: false });
      }
    },

    openEdit: (row) => {
      set({ editError: null });
      const { android, ios } = platformBooleansFromDoc(row.platforms);
      set({
        edit: {
          id: row.id,
          title: row.title,
          description: row.description ?? "",
          targetLink: row.targetLink,
          startsAtLocal: row.startsAt
            ? timestampToDatetimeLocalValue(row.startsAt)
            : "",
          expiresAtLocal: timestampToDatetimeLocalValue(row.expiresAt),
          active: row.active,
          currentImageUrl: row.imageUrl,
          platformAndroid: android,
          platformIos: ios,
        },
      });
    },

    openEditFromLive: (live) => {
      const draftById = draftByIdFromList(get().items);
      if (draftById.has(live.id)) {
        set({ editFromLiveOriginal: null });
        get().openEdit(draftById.get(live.id)!);
        return;
      }
      set({ editFromLiveOriginal: live });
      get().openEdit(live);
    },

    closeEdit: () =>
      set({
        edit: null,
        editError: null,
        editFromLiveOriginal: null,
      }),

    saveEdit: async (file, uid, actor) => {
      const { edit, editFromLiveOriginal } = get();
      if (!edit) return;
      set({ editError: null });

      if (editFromLiveOriginal) {
        const orig = editFromLiveOriginal;
        const origExpires = timestampToDatetimeLocalValue(orig.expiresAt);
        const origStarts = orig.startsAt
          ? timestampToDatetimeLocalValue(orig.startsAt)
          : "";
        if (
          editFromLiveIsUnchanged(edit, orig, origExpires, origStarts, file)
        ) {
          get().closeEdit();
          return;
        }
        set({ editSaving: true });
        try {
          const platforms = platformsFromBooleans(
            edit.platformAndroid,
            edit.platformIos,
          );
          if (!platforms) {
            set({
              editError: "Pick at least one platform — Android and/or iOS.",
            });
            set({ editSaving: false });
            return;
          }
          await updateLiveHappeningNowItem(
            edit.id,
            uid,
            {
              title: edit.title,
              description: edit.description || undefined,
              targetLink: edit.targetLink,
              startsAtLocal: edit.startsAtLocal || undefined,
              expiresAtLocal: edit.expiresAtLocal,
              active: edit.active,
              platforms,
              imageFile: file ?? undefined,
            },
            actor,
          );
          set({ success: "Live card updated." });
          get().closeEdit();
          await get().load();
        } catch (err: unknown) {
          set({
            editError: err instanceof Error ? err.message : "Could not update",
          });
        } finally {
          set({ editSaving: false });
        }
        return;
      }

      set({ editSaving: true });
      try {
        const platforms = platformsFromBooleans(
          edit.platformAndroid,
          edit.platformIos,
        );
        if (!platforms) {
          set({
            editError: "Pick at least one platform — Android and/or iOS.",
          });
          set({ editSaving: false });
          return;
        }
        await updateHappeningNowItem(
          edit.id,
          uid,
          {
            title: edit.title,
            description: edit.description || undefined,
            targetLink: edit.targetLink,
            startsAtLocal: edit.startsAtLocal || undefined,
            expiresAtLocal: edit.expiresAtLocal,
            active: edit.active,
            platforms,
            imageFile: file ?? undefined,
          },
          actor,
        );
        set({ success: "Draft card updated." });
        get().closeEdit();
        await get().load();
      } catch (err: unknown) {
        set({
          editError: err instanceof Error ? err.message : "Could not update",
        });
      } finally {
        set({ editSaving: false });
      }
    },

    deleteEdit: async (actor) => {
      const { edit, editFromLiveOriginal } = get();
      if (!edit) return;
      const fromLive = Boolean(editFromLiveOriginal);
      const ok = window.confirm(
        fromLive
          ? `Remove "${edit.title}" from the live carousel? Users will stop seeing it immediately.`
          : `Remove "${edit.title}" from the draft? It stays live until you publish again.`,
      );
      if (!ok) return;
      set({ editDeleting: true, editError: null });
      try {
        if (fromLive) {
          await deleteLiveHappeningNowItem(edit.id, actor);
          set({ success: "Card removed from the live carousel." });
        } else {
          await deleteHappeningNowItem(edit.id, actor);
          set({ success: "Card removed from draft." });
        }
        get().closeEdit();
        await get().load();
      } catch (err: unknown) {
        set({
          editError: err instanceof Error ? err.message : "Could not delete",
        });
      } finally {
        set({ editDeleting: false });
      }
    },

    toggleDraftActive: async (item, actor) => {
      set({ busyId: item.id });
      try {
        await setHappeningNowActive(item.id, !item.active, actor);
        await get().load();
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not toggle",
        });
      } finally {
        set({ busyId: null });
      }
    },

    deleteDraft: async (item, actor) => {
      const ok = window.confirm(
        `Remove "${item.title}" from the draft? It stays live until you publish again.`,
      );
      if (!ok) return;
      set({ busyId: item.id });
      try {
        await deleteHappeningNowItem(item.id, actor);
        await get().load();
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not delete",
        });
      } finally {
        set({ busyId: null });
      }
    },

    onDraftDragEnd: async (event, actor) => {
      const { active, over } = event;
      const { items } = get();
      if (!over || active.id === over.id) return;
      const fromIdx = items.findIndex((i) => i.id === active.id);
      const toIdx = items.findIndex((i) => i.id === over.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = arrayMove(items, fromIdx, toIdx);
      set({ items: next });
      try {
        await reorderHappeningNowItems(
          next.map((i) => i.id),
          actor,
        );
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not reorder",
        });
        await get().load();
      }
    },

    toggleLiveActive: async (item, actor) => {
      set({ liveBusyId: item.id });
      try {
        await setLiveHappeningNowActive(item.id, !item.active, actor);
        await get().load();
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not toggle",
        });
      } finally {
        set({ liveBusyId: null });
      }
    },

    onLiveDragEnd: async (event, actor) => {
      const { active, over } = event;
      const { liveItems } = get();
      if (!over || active.id === over.id) return;
      const fromIdx = liveItems.findIndex((i) => i.id === active.id);
      const toIdx = liveItems.findIndex((i) => i.id === over.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = arrayMove(liveItems, fromIdx, toIdx);
      set({ liveItems: next });
      try {
        await reorderLiveHappeningNowItems(
          next.map((i) => i.id),
          actor,
        );
        await get().load();
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not reorder live",
        });
        await get().load();
      }
    },

    publishCarousel: async (uid, actor, opts) => {
      const { items, liveItems } = get();
      const removed = liveCardsRemovedOnPublish(items, liveItems);
      // Callers that show their own confirmation (e.g. the carousel composer's
      // ConfirmDialog) pass skipConfirm to avoid a double prompt.
      if (!opts?.skipConfirm && removed.length > 0) {
        const titles = removed
          .slice(0, 4)
          .map((c) => `"${c.title}"`)
          .join(", ");
        const more =
          removed.length > 4 ? ` and ${removed.length - 4} more` : "";
        const ok = window.confirm(
          `Publishing will remove ${removed.length} ${
            removed.length === 1 ? "card" : "cards"
          } from the live carousel: ${titles}${more}.\n\nContinue?`,
        );
        if (!ok) return;
      }

      set({ publishing: true, error: null, success: null });
      try {
        const result = await useCarouselPublicationsStore
          .getState()
          .publish(uid, actor);
        set({
          success: `Published — live carousel is now ${result.cardCount} ${
            result.cardCount === 1 ? "card" : "cards"
          }. Draft cleared.`,
        });
        await get().load();
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not publish",
        });
      } finally {
        set({ publishing: false });
      }
    },

    seedFromLive: async (uid, actor) => {
      set({ seeding: true, error: null });
      try {
        const { count } = await seedDraftFromLive(uid, actor);
        if (count === 0) {
          set({ success: "Live carousel is empty — nothing to seed." });
        } else {
          set({
            success: `Pulled ${count} ${
              count === 1 ? "card" : "cards"
            } from live into the draft.`,
          });
        }
        await get().load();
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not seed draft",
        });
      } finally {
        set({ seeding: false });
      }
    },

    clearDraft: async (actor) => {
      const { items } = get();
      if (items.length === 0) return;
      const ok = window.confirm(
        `Clear the entire draft? This removes all ${items.length} ${
          items.length === 1 ? "card" : "cards"
        } from the working draft. The live carousel stays untouched until you publish.`,
      );
      if (!ok) return;
      set({ clearing: true, error: null, success: null });
      try {
        const { count } = await clearDraftService(actor);
        set({
          success: `Cleared draft (${count} ${
            count === 1 ? "card" : "cards"
          } removed).`,
        });
        await get().load();
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not clear draft",
        });
      } finally {
        set({ clearing: false });
      }
    },

    loadPublicationPreview: async (pub) => {
      set({ pubBusyId: pub.id });
      try {
        const full = await getCarouselPublication(pub.id);
        if (full) set({ previewPub: full });
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not open preview",
        });
      } finally {
        set({ pubBusyId: null });
      }
    },

    closePublicationPreview: () => set({ previewPub: null }),

    openRepublish: async (pub) => {
      set({ pubBusyId: pub.id });
      try {
        const full = await getCarouselPublication(pub.id);
        if (full) {
          set({ republishError: null, republishTarget: full });
        }
      } catch (err: unknown) {
        set({
          error:
            err instanceof Error ? err.message : "Could not open republish",
        });
      } finally {
        set({ pubBusyId: null });
      }
    },

    closeRepublish: () => {
      if (get().republishSubmitting) return;
      set({ republishTarget: null });
    },

    confirmRepublish: async (params, uid, actor) => {
      const { republishTarget } = get();
      if (!republishTarget) return;
      set({ republishSubmitting: true, republishError: null });
      try {
        const newExpiresAtDate = new Date(params.newExpiresAtLocal);
        if (Number.isNaN(newExpiresAtDate.getTime())) {
          throw new Error("Pick a valid expiry date.");
        }
        const result = await useCarouselPublicationsStore
          .getState()
          .republishToDraft(
            republishTarget.id,
            {
              includeCardIds: params.includeCardIds,
              newExpiresAt: Timestamp.fromDate(newExpiresAtDate),
              mode: "replace",
            },
            uid,
            actor,
          );
        set({
          success: `Restored ${result.restoredCount} ${
            result.restoredCount === 1 ? "card" : "cards"
          } to draft. Click Publish carousel to push them live.`,
          republishTarget: null,
        });
        await get().load();
      } catch (err: unknown) {
        set({
          republishError:
            err instanceof Error ? err.message : "Could not restore",
        });
      } finally {
        set({ republishSubmitting: false });
      }
    },

    togglePublicationStar: async (pub, uid, actor) => {
      set({ pubBusyId: pub.id });
      try {
        await useCarouselPublicationsStore
          .getState()
          .setStarred(pub.id, !pub.starredAt, uid, actor);
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not update star",
        });
      } finally {
        set({ pubBusyId: null });
      }
    },

    deletePublication: async (pub, actor) => {
      const ok = window.confirm(
        `Delete this carousel from history? It was published ${new Date(
          pub.publishedAt.toMillis(),
        ).toLocaleString()} and had ${pub.cardCount} ${
          pub.cardCount === 1 ? "card" : "cards"
        }.`,
      );
      if (!ok) return;
      set({ pubBusyId: pub.id });
      try {
        await useCarouselPublicationsStore.getState().remove(pub.id, actor);
      } catch (err: unknown) {
        set({
          error: err instanceof Error ? err.message : "Could not delete",
        });
      } finally {
        set({ pubBusyId: null });
      }
    },
  }),
);
